import http from "@/helper/http";
import api from "@/helper/api";

const CATEGORY_RULES = [
  {
    key: "catalog",
    label: "Catalog & Pricing",
    keywords: ["PRODUCT", "SERVICE", "INVENTORY", "PROMOS"],
  },
  {
    key: "purchases",
    label: "Purchasing & Suppliers",
    keywords: ["PURCHASE", "SUPPLIER", "ORDER", "RECEIPT"],
  },
  {
    key: "sales",
    label: "Sales & Carts",
    keywords: ["SALE", "TICKET", "CART"],
  },
  {
    key: "finance",
    label: "Finance & Reporting",
    keywords: ["EXPENSE", "REPORT", "REFUND", "PROFIT"],
  },
  { key: "customers", label: "Customers & CRM", keywords: ["CUSTOMER"] },
  { key: "team", label: "Team & Access", keywords: ["USER", "DASHBOARD"] },
  { key: "compliance", label: "Compliance", keywords: ["RRA", "EBM"] },
  { key: "settings", label: "Settings", keywords: ["SETTING"] },
];

const CATEGORY_LABEL_OVERRIDES = {
  standard: "Standard Access",
  cashier: "Cashier",
  auditor: "Auditor",
  manager: "Manager",
};

const WRITE_ACTION_PATTERN = /(WRITE|MANAGE|APPROVE|PROCESS|CHANGE|LOCK)/i;

const PRESET_DEFINITIONS = [
  {
    key: "observer",
    label: "Store Observer",
    description:
      "Read-only access to catalog, customers and dashboards for rapid diagnostics.",
    permissions: [
      "CAN_READ_PRODUCTS",
      "CAN_READ_SERVICES",
      "CAN_READ_PURCHASES",
      "CAN_READ_EXPENSES",
      "CAN_READ_REPORTS",
      "CAN_READ_SALE_CARTS",
      "CAN_READ_CUSTOMERS",
      "CAN_READ_PROMO_CODES",
      "CAN_READ_TICKET_CARTS",
      "CAN_SHARE_DASHBOARDS",
      "CAN_READ_ASSETS",
    ],
  },
  {
    key: "catalog_manager",
    label: "Catalog Manager",
    description:
      "Create and maintain products, services and promotional pricing.",
    permissions: [
      "CAN_READ_PRODUCTS",
      "CAN_WRITE_PRODUCTS",
      "CAN_READ_SERVICES",
      "CAN_WRITE_SERVICES",
      "CAN_READ_PROMO_CODES",
      "CAN_WRITE_PROMO_CODES",
    ],
  },
  {
    key: "operations_lead",
    label: "Operations Lead",
    description:
      "Oversee purchasing, expenses and compliance workflows while collaborating with teams.",
    permissions: [
      "CAN_READ_PURCHASES",
      "CAN_WRITE_PURCHASES",
      "CAN_MANAGE_PURCHASE_SUPPLIERS",
      "CAN_MANAGE_PURCHASE_ORDERS",
      "CAN_WRITE_PURCHASE_RECEIPTS",
      "CAN_READ_EXPENSES",
      "CAN_WRITE_EXPENSES",
      "CAN_READ_ASSETS",
      "CAN_WRITE_ASSETS",
      "CAN_CHANGE_ASSET_CONDITION",
      "CAN_PROCESS_REFUNDS",
      "CAN_MANAGE_RRA_EBM_DATA",
    ],
  },
  {
    key: "acting_manager",
    label: "Acting Store Manager",
    description:
      "Broad control to steer operations, manage teams and fine-tune store settings.",
    permissions: [
      "CAN_MANAGE_USERS",
      "CAN_CHANGE_STORE_SETTINGS",
      "CAN_CHANGE_EXPENSE_CREATION_TIME",
      "CAN_CHANGE_SALE_CARTS_CREATION_TIME",
      "CAN_CHANGE_PURCHASES_CREATION_TIME",
      "CAN_WRITE_REPORTS",
      "CAN_READ_PROFITABILITY",
      "CAN_WRITE_SALE_CARTS",
      "CAN_WRITE_CUSTOMERS",
      "CAN_WRITE_TICKET_CARTS",
      "CAN_LOCK_TICKET_CARTS",
      "CAN_UNLOCK_TICKET_CARTS",
      "CAN_WRITE_PRODUCTS",
      "CAN_WRITE_SERVICES",
      "CAN_WRITE_PURCHASES",
      "CAN_WRITE_EXPENSES",
      "CAN_MANAGE_PURCHASE_SUPPLIERS",
      "CAN_MANAGE_PURCHASE_ORDERS",
    ],
  },
];

function toTitleCase(value = "") {
  return value
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function normalizePermissionName(name = "") {
  const normalized = String(name || "").toUpperCase();
  if (normalized.startsWith("CAN_")) {
    return normalized;
  }
  if (normalized.includes("CAN_")) {
    return normalized.slice(normalized.indexOf("CAN_"));
  }
  return normalized;
}

function detectCategory(permissionName) {
  const upperName = permissionName.toUpperCase();
  const rule = CATEGORY_RULES.find(({ keywords }) =>
    keywords.some((keyword) => upperName.includes(keyword)),
  );
  return rule ? rule.key : "general";
}

function displayCategoryName(categoryKey) {
  if (CATEGORY_LABEL_OVERRIDES[categoryKey]) {
    return CATEGORY_LABEL_OVERRIDES[categoryKey];
  }
  const matched = CATEGORY_RULES.find((rule) => rule.key === categoryKey);
  if (matched) {
    return matched.label;
  }
  return "General";
}

function buildPermissionOption(name, label = null) {
  const normalized = normalizePermissionName(name);
  const readable = normalized.replace(/^CAN_/, "").replace(/_/g, " ");
  return {
    value: normalized,
    label: label || toTitleCase(readable),
    category: detectCategory(normalized),
    readOnly: !WRITE_ACTION_PATTERN.test(normalized),
  };
}

function buildPreset(presetDefinition) {
  const uniquePermissions = Array.from(
    new Set(
      presetDefinition.permissions.map((name) => normalizePermissionName(name)),
    ),
  );

  return {
    ...presetDefinition,
    permissions: uniquePermissions,
    readOnly: uniquePermissions.every(
      (name) => !WRITE_ACTION_PATTERN.test(name),
    ),
  };
}

export default {
  state: {
    jackActiveAccesses: [],
    jackActiveFilters: {
      storeId: null,
      targetUserId: null,
      query: "",
    },
    loadingActiveAccesses: false,
    creatingAccess: false,
    revokingAccess: false,
    jackLogs: [],
    loadingLogs: false,
    permissionOptions: [],
    loadingPermissionOptions: false,
    permissionCategories: {},
    permissionPresets: PRESET_DEFINITIONS.map((preset) => buildPreset(preset)),
    lastActiveSyncAt: null,
    lastLogsSyncAt: null,
    storeMembersByStore: {},
    loadingStoreMembers: false,
  },
  mutations: {
    SET_JACK_ACTIVE_FILTERS(state, filters) {
      state.jackActiveFilters = {
        ...state.jackActiveFilters,
        ...filters,
      };
    },
    SET_JACK_LOADING_ACTIVE_ACCESSES(state, loading) {
      state.loadingActiveAccesses = loading;
    },
    SET_JACK_ACTIVE_ACCESSES(state, accesses) {
      state.jackActiveAccesses = accesses;
      state.lastActiveSyncAt = Date.now();
    },
    UPSERT_JACK_ACCESS(state, access) {
      const index = state.jackActiveAccesses.findIndex(
        (item) => item.id === access.id,
      );
      if (index === -1) {
        state.jackActiveAccesses = [access, ...state.jackActiveAccesses];
        return;
      }
      const updated = [...state.jackActiveAccesses];
      updated.splice(index, 1, access);
      state.jackActiveAccesses = updated;
    },
    REMOVE_JACK_ACCESS(state, accessId) {
      state.jackActiveAccesses = state.jackActiveAccesses.filter(
        (item) => item.id !== accessId,
      );
    },
    SET_JACK_CREATING_ACCESS(state, loading) {
      state.creatingAccess = loading;
    },
    SET_JACK_REVOKING_ACCESS(state, loading) {
      state.revokingAccess = loading;
    },
    SET_JACK_LOGS(state, logs) {
      state.jackLogs = logs;
      state.lastLogsSyncAt = Date.now();
    },
    SET_JACK_LOADING_LOGS(state, loading) {
      state.loadingLogs = loading;
    },
    SET_JACK_LOADING_PERMISSION_OPTIONS(state, loading) {
      state.loadingPermissionOptions = loading;
    },
    SET_JACK_PERMISSION_OPTIONS(state, options) {
      state.permissionOptions = options;
    },
    SET_JACK_PERMISSION_CATEGORIES(state, categories) {
      state.permissionCategories = categories || {};
    },
    SET_JACK_STORE_MEMBERS(state, { storeId, members }) {
      state.storeMembersByStore = {
        ...state.storeMembersByStore,
        [storeId]: members,
      };
    },
    SET_JACK_LOADING_STORE_MEMBERS(state, loading) {
      state.loadingStoreMembers = loading;
    },
  },
  actions: {
    async fetchJackPermissionOptions({ commit, state }) {
      if (state.permissionOptions.length) {
        return state.permissionOptions;
      }
      commit("SET_JACK_LOADING_PERMISSION_OPTIONS", true);
      try {
        const response = await http.get(api.store_permissions_catalog);
        const catalog = response?.data || {};

        const aggregatedNames = new Map();
        Object.values(catalog).forEach((collection) => {
          (collection || []).forEach((entry) => {
            const name = normalizePermissionName(
              entry?.standardized_name || entry?.name,
            );
            if (name) {
              aggregatedNames.set(
                name,
                entry?.label || aggregatedNames.get(name) || null,
              );
            }
          });
        });

        const options = Array.from(aggregatedNames.entries())
          .map(([name, label]) => buildPermissionOption(name, label))
          .sort((a, b) => a.label.localeCompare(b.label));

        commit("SET_JACK_PERMISSION_OPTIONS", options);
        commit("SET_JACK_PERMISSION_CATEGORIES", catalog);
        return options;
      } finally {
        commit("SET_JACK_LOADING_PERMISSION_OPTIONS", false);
      }
    },
    async fetchJackActiveAccesses({ commit }, { storeId, targetUserId } = {}) {
      commit("SET_JACK_LOADING_ACTIVE_ACCESSES", true);
      commit("SET_JACK_ACTIVE_FILTERS", { storeId, targetUserId });
      try {
        const params = {};
        if (storeId) {
          params.store_id = storeId;
        }
        if (targetUserId) {
          params.target_user_id = targetUserId;
        }
        const response = await http.get(api.admin_jack_active_accesses, {
          params,
        });
        const payload = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        commit("SET_JACK_ACTIVE_ACCESSES", payload);
        return payload;
      } finally {
        commit("SET_JACK_LOADING_ACTIVE_ACCESSES", false);
      }
    },
    async createJackStoreAccess({ commit }, payload) {
      commit("SET_JACK_CREATING_ACCESS", true);
      try {
        const response = await http.post(api.admin_jack_store_access, payload);
        const newAccess = response?.data;
        if (newAccess) {
          commit("UPSERT_JACK_ACCESS", newAccess);
        }
        return newAccess;
      } finally {
        commit("SET_JACK_CREATING_ACCESS", false);
      }
    },
    async revokeJackStoreAccess({ commit }, { jackAccessId }) {
      commit("SET_JACK_REVOKING_ACCESS", true);
      try {
        const response = await http.post(api.admin_jack_store_access_revoke, {
          jack_access_id: jackAccessId,
        });
        const revoked = response?.data;
        if (revoked) {
          commit("REMOVE_JACK_ACCESS", revoked.id);
        }
        return revoked;
      } finally {
        commit("SET_JACK_REVOKING_ACCESS", false);
      }
    },
    async fetchJackLogs({ commit }, { jackAccessId, storeId } = {}) {
      commit("SET_JACK_LOADING_LOGS", true);
      try {
        const params = {};
        if (jackAccessId) {
          params.jack_access_id = jackAccessId;
        }
        if (storeId) {
          params.store_id = storeId;
        }
        const response = await http.get(api.admin_jack_logs, { params });
        const logs = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        commit("SET_JACK_LOGS", logs);
        return logs;
      } finally {
        commit("SET_JACK_LOADING_LOGS", false);
      }
    },
    async fetchJackStoreMembers(
      { state, commit },
      { storeId, force = false } = {},
    ) {
      if (!storeId) {
        return [];
      }
      if (!force && state.storeMembersByStore[storeId]) {
        return state.storeMembersByStore[storeId];
      }
      commit("SET_JACK_LOADING_STORE_MEMBERS", true);
      try {
        const response = await http.get(api.store_users_permissions(storeId));
        const members = Array.isArray(response?.data) ? response.data : [];
        commit("SET_JACK_STORE_MEMBERS", { storeId, members });
        return members;
      } finally {
        commit("SET_JACK_LOADING_STORE_MEMBERS", false);
      }
    },
    async searchJackStores(_, { query = "", page = 1, perPage = 10 } = {}) {
      const params = {
        page,
        per_page: perPage,
      };

      if (query) {
        params.search = query;
      }

      const response = await http.get(api.admin_jack_stores, { params });
      const payload = response?.data || {};

      return {
        items: Array.isArray(payload.items) ? payload.items : [],
        total: payload.total ?? 0,
        page: payload.page ?? page,
        pages: payload.pages ?? 0,
        perPage: payload.per_page ?? perPage,
        hasNext: payload.has_next ?? false,
        hasPrev: payload.has_prev ?? false,
        extras: payload.extras || {},
      };
    },
  },
  getters: {
    jackActiveAccesses: (state) => state.jackActiveAccesses,
    jackActiveFilters: (state) => state.jackActiveFilters,
    isLoadingJackActiveAccesses: (state) => state.loadingActiveAccesses,
    isCreatingJackAccess: (state) => state.creatingAccess,
    isRevokingJackAccess: (state) => state.revokingAccess,
    jackLogs: (state) => state.jackLogs,
    isLoadingJackLogs: (state) => state.loadingLogs,
    jackPermissionOptions: (state) => state.permissionOptions,
    isLoadingJackPermissionOptions: (state) => state.loadingPermissionOptions,
    jackPermissionPresets: (state) => state.permissionPresets,
    jackStoreMembers: (state) => (storeId) =>
      state.storeMembersByStore[storeId] || [],
    isLoadingJackStoreMembers: (state) => state.loadingStoreMembers,
    jackLastActiveSyncAt: (state) => state.lastActiveSyncAt,
    jackLastLogsSyncAt: (state) => state.lastLogsSyncAt,
    jackPermissionGroups: (state) => {
      const catalog = state.permissionCategories || {};
      const optionMap = new Map(
        state.permissionOptions.map((option) => [option.value, option]),
      );
      const groups = [];
      const orderedKeys = ["standard", "cashier", "auditor", "manager"];

      orderedKeys.forEach((key) => {
        const entries = catalog[key] || [];
        const options = entries
          .map((entry) => {
            const normalized = normalizePermissionName(
              entry?.standardized_name || entry?.name,
            );
            return optionMap.get(normalized);
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));

        if (options.length) {
          const readOnlyCount = options.filter(
            (option) => option.readOnly,
          ).length;
          const writeCount = options.length - readOnlyCount;
          groups.push({
            key,
            label: displayCategoryName(key),
            readOnlyCount,
            writeCount,
            options,
          });
        }
      });

      return groups;
    },
  },
};
